import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthLayout, AuthFooterLink } from "@/features/auth/components/AuthLayout";
import { FormField } from "@/components/FormField";
import { SubmitButton } from "@/components/SubmitButton";
import { StatusBanner } from "@/components/StatusBanner";
import { useAuth } from "@/hooks/useAuth";
import { signInWithEmail } from "@/features/auth/authApi";
import { validateLogin } from "@/features/auth/validation";
import { friendlyAuthError, SESSION_EXPIRED } from "@/lib/errors";
import { ROUTES } from "@/constants/routes";

export default function LoginPage() {
  const { sessionExpired } = useAuth();
  const { search } = useLocation();
  const [values, setValues] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const found = validateLogin(values);
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) return;
    setSubmitting(true);
    const { error } = await signInWithEmail(values).catch((err) => ({ error: err }));
    if (error) {
      setFormError(friendlyAuthError(error));
      setSubmitting(false);
    }
    // On success PublicOnlyRoute redirects to ?next or /dashboard.
  };

  return (
    <AuthLayout
      eyebrow="Sign in"
      title="Welcome back."
      subtitle="Sign in to manage your MetWorth identity."
      footer={<AuthFooterLink text="New to MetWorth?" to={`${ROUTES.signup}${search}`} label="Create your MetWorth" testId="login-to-signup-link" />}
    >
      <form data-testid="login-form" onSubmit={onSubmit} noValidate className="space-y-6">
        {sessionExpired && !formError && (
          <StatusBanner variant="info" testId="session-expired-banner">{SESSION_EXPIRED}</StatusBanner>
        )}
        {formError && <StatusBanner testId="login-error-banner">{formError}</StatusBanner>}
        <FormField id="login-email" name="email" type="email" label="Email" autoComplete="email" testId="login-email-input" value={values.email} onChange={onChange} error={errors.email} />
        <FormField id="login-password" name="password" type="password" label="Password" autoComplete="current-password" testId="login-password-input" value={values.password} onChange={onChange} error={errors.password} />
        <SubmitButton loading={submitting} loadingLabel="Signing in" testId="login-submit-button">
          Sign in
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
